import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { StatCard } from "@how/ui";
import { useCampuses } from "../campuses/campus-queries";
import { useGroups } from "../groups/group-queries";
import { useTags } from "../people/tag-queries";
import {
  type AudienceChannel,
  type AudienceDefinition,
  type AudienceFilter,
  type AudienceRow,
  type CustomFieldOp,
  type MembershipStanding,
  STANDING_LABEL,
  useAudienceCounts,
  useCustomFieldDefinitions,
  useSaveAudience,
} from "./audience-queries";

const FILTER_TYPES: readonly { value: AudienceFilter["type"]; label: string }[] = [
  { value: "tag", label: "Has a tag" },
  { value: "campus", label: "Home campus" },
  { value: "group", label: "In a group" },
  { value: "standing", label: "Membership standing" },
  { value: "household_role", label: "Household role" },
  { value: "custom_field", label: "Custom field" },
];

const OPS_BY_TYPE: Record<string, readonly { value: CustomFieldOp; label: string }[]> = {
  text: [
    { value: "equals", label: "is" },
    { value: "contains", label: "contains" },
  ],
  long_text: [
    { value: "equals", label: "is" },
    { value: "contains", label: "contains" },
  ],
  number: [
    { value: "equals", label: "is" },
    { value: "before", label: "is less than" },
    { value: "after", label: "is more than" },
  ],
  date: [
    { value: "equals", label: "is on" },
    { value: "before", label: "is before" },
    { value: "after", label: "is after" },
  ],
  boolean: [
    { value: "is_true", label: "is yes" },
    { value: "is_false", label: "is no" },
  ],
  select: [{ value: "any_of", label: "is any of" }],
  multi_select: [{ value: "any_of", label: "includes any of" }],
};

function emptyFilter(type: AudienceFilter["type"]): AudienceFilter {
  switch (type) {
    case "tag":
      return { type, tag_ids: [] };
    case "campus":
      return { type, campus_ids: [] };
    case "group":
      return { type, group_ids: [] };
    case "standing":
      return { type, values: [] };
    case "household_role":
      return { type, values: [] };
    case "custom_field":
      return { type, definition_id: "", op: "equals", value: "" };
  }
}

/** A filter the database would accept: every list has something in it, a custom field has its field and value. */
function complete(filter: AudienceFilter): boolean {
  switch (filter.type) {
    case "tag":
      return filter.tag_ids.length > 0;
    case "campus":
      return filter.campus_ids.length > 0;
    case "group":
      return filter.group_ids.length > 0;
    case "standing":
    case "household_role":
      return filter.values.length > 0;
    case "custom_field":
      return (
        filter.definition_id !== "" &&
        (filter.op === "is_true" ||
          filter.op === "is_false" ||
          (Array.isArray(filter.value) ? filter.value.length > 0 : (filter.value ?? "") !== ""))
      );
  }
}

/**
 * The audience editor (communications plan task 3): name it, add filters, and watch the counts. The counts are the
 * point: a church learns here, before any send, that two thirds of its directory never agreed to SMS.
 */
export function AudienceModal({
  opened,
  audience,
  onClose,
}: {
  readonly opened: boolean;
  readonly audience: AudienceRow | null;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState(audience?.name ?? "");
  const [description, setDescription] = useState(audience?.description ?? "");
  const [filters, setFilters] = useState<readonly AudienceFilter[]>(
    audience?.definition.filters ?? [],
  );
  const [channel, setChannel] = useState<AudienceChannel>("sms");
  const save = useSaveAudience();
  const tags = useTags();
  const campuses = useCampuses();
  const groups = useGroups({ search: "", status: "active" });
  const fields = useCustomFieldDefinitions();

  const definition: AudienceDefinition = { filters: filters.filter(complete) };
  const counts = useAudienceCounts(definition, channel);
  const incomplete = filters.some((f) => !complete(f));

  const update = (index: number, next: AudienceFilter) => {
    setFilters((current) => current.map((f, i) => (i === index ? next : f)));
  };

  const renderFilter = (filter: AudienceFilter, index: number) => {
    switch (filter.type) {
      case "tag":
        return (
          <MultiSelect
            label="Tags"
            data={(tags.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={[...filter.tag_ids]}
            onChange={(value) => {
              update(index, { type: "tag", tag_ids: value });
            }}
            searchable
          />
        );
      case "campus":
        return (
          <MultiSelect
            label="Campuses"
            data={(campuses.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={[...filter.campus_ids]}
            onChange={(value) => {
              update(index, { type: "campus", campus_ids: value });
            }}
          />
        );
      case "group":
        return (
          <MultiSelect
            label="Groups (current season, active members)"
            data={(groups.data ?? []).map((g) => ({ value: g.id, label: g.name }))}
            value={[...filter.group_ids]}
            onChange={(value) => {
              update(index, { type: "group", group_ids: value });
            }}
            searchable
          />
        );
      case "standing":
        return (
          <MultiSelect
            label="Standing"
            data={(Object.keys(STANDING_LABEL) as MembershipStanding[]).map((s) => ({
              value: s,
              label: STANDING_LABEL[s],
            }))}
            value={[...filter.values]}
            onChange={(value) => {
              update(index, { type: "standing", values: value as MembershipStanding[] });
            }}
          />
        );
      case "household_role":
        return (
          <MultiSelect
            label="Household role"
            data={[
              { value: "adult", label: "Adult in a household" },
              { value: "child", label: "Child in a household" },
            ]}
            value={[...filter.values]}
            onChange={(value) => {
              update(index, { type: "household_role", values: value as ("adult" | "child")[] });
            }}
          />
        );
      case "custom_field": {
        const field = (fields.data ?? []).find((f) => f.id === filter.definition_id);
        const ops = field === undefined ? [] : (OPS_BY_TYPE[field.field_type] ?? []);
        const choice = field?.field_type === "select" || field?.field_type === "multi_select";
        return (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
            <Select
              label="Field"
              data={(fields.data ?? []).map((f) => ({ value: f.id, label: f.label }))}
              value={filter.definition_id === "" ? null : filter.definition_id}
              onChange={(value) => {
                const next = (fields.data ?? []).find((f) => f.id === value);
                const op =
                  next === undefined
                    ? "equals"
                    : (OPS_BY_TYPE[next.field_type]?.[0]?.value ?? "equals");
                update(index, {
                  type: "custom_field",
                  definition_id: value ?? "",
                  op,
                  value:
                    next?.field_type === "select" || next?.field_type === "multi_select" ? [] : "",
                });
              }}
            />
            <Select
              label="Comparison"
              data={ops}
              value={filter.op}
              disabled={field === undefined}
              onChange={(value) => {
                update(index, { ...filter, op: (value as CustomFieldOp | null) ?? filter.op });
              }}
            />
            {filter.op === "is_true" || filter.op === "is_false" ? null : choice ? (
              <MultiSelect
                label="Options"
                data={field.options.map((o) => ({ value: o, label: o }))}
                value={typeof filter.value === "object" ? [...filter.value] : []}
                onChange={(value) => {
                  update(index, { ...filter, value });
                }}
              />
            ) : (
              <TextInput
                label="Value"
                type={
                  field?.field_type === "date"
                    ? "date"
                    : field?.field_type === "number"
                      ? "number"
                      : "text"
                }
                value={typeof filter.value === "string" ? filter.value : ""}
                onChange={(event) => {
                  update(index, { ...filter, value: event.currentTarget.value });
                }}
              />
            )}
          </SimpleGrid>
        );
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={audience === null ? "New audience" : "Edit audience"}
      size="xl"
    >
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Name"
            required
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
          <Textarea
            label="Description"
            autosize
            minRows={1}
            value={description}
            onChange={(event) => {
              setDescription(event.currentTarget.value);
            }}
          />
        </SimpleGrid>

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Who is in it
          </Text>
          <Text size="sm" c="dimmed">
            Everyone in the church, narrowed by each filter you add. All filters must match; within
            one filter, any of the choices counts. Children are never messaged, whatever the filters
            say.
          </Text>
          {filters.map((filter, index) => (
            <Group key={index} align="flex-end" wrap="nowrap" gap="xs">
              <div style={{ flex: 1 }}>{renderFilter(filter, index)}</div>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Remove filter"
                onClick={() => {
                  setFilters((current) => current.filter((_, i) => i !== index));
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group>
            <Select
              placeholder="Add a filter"
              data={FILTER_TYPES}
              value={null}
              onChange={(value) => {
                if (value !== null)
                  setFilters((current) => [
                    ...current,
                    emptyFilter(value as AudienceFilter["type"]),
                  ]);
              }}
              leftSection={<IconPlus size={14} />}
              w={240}
            />
          </Group>
        </Stack>

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Who it reaches
            </Text>
            <SegmentedControl
              size="xs"
              value={channel}
              onChange={(value) => {
                setChannel(value === "email" ? "email" : "sms");
              }}
              data={[
                { value: "sms", label: "By text" },
                { value: "email", label: "By email" },
              ]}
            />
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
            <StatCard label="Matched" value={(counts.data?.matched ?? 0).toLocaleString()} />
            <StatCard label="Reachable" value={(counts.data?.reachable ?? 0).toLocaleString()} />
            <StatCard label="Not agreed" value={(counts.data?.notAgreed ?? 0).toLocaleString()} />
            <StatCard
              label={channel === "sms" ? "No phone" : "No email"}
              value={(counts.data?.noAddress ?? 0).toLocaleString()}
            />
            <StatCard label="Children" value={(counts.data?.children ?? 0).toLocaleString()} />
          </SimpleGrid>
          <Text size="xs" c="dimmed">
            Not agreed: they have an address and have not said yes; these are the people to ask. No
            address: asking will not help. Children: never reached.
          </Text>
        </Stack>

        {save.isError ? (
          <Alert color="red" variant="light">
            {save.error.message}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={name.trim() === "" || incomplete}
            onClick={() => {
              save.mutate(
                { audienceId: audience?.id ?? null, name: name.trim(), description, definition },
                { onSuccess: onClose },
              );
            }}
          >
            Save audience
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
